/**
 * Tiered LiveKit reconnect UX: avoid blocking modals during recoverable / high-latency episodes.
 * Decisions are centralized here; LiveKitRoomConnector dispatches via dispatchLivekitReconnectUx.
 */

import store from '../stores'
import { setLiveKitReconnectState } from '../stores/UserStore'
import type { LivekitReconnectUxConfig } from '../config/mediaConfig'
import { getLivekitReconnectUxConfig } from '../config/mediaConfig'

export type ReconnectUxTier = 'silent' | 'banner' | 'modal'

export interface ReconnectUxDecision {
  tier: ReconnectUxTier
  redux: {
    showPrompt: boolean
    message: string
    showReconnectionDialog?: boolean
    cooldownUntil?: number
    reconnectAttempt?: number
    maxAttempts?: number
  }
  notification?: { type: 'error' | 'info' | 'warning'; message: string; duration: number }
}

let lastModalAt = 0
let retryableErrorFirstAt = 0
let retryableErrorCount = 0

export function resetLivekitReconnectUxPolicy(): void {
  lastModalAt = 0
  retryableErrorFirstAt = 0
  retryableErrorCount = 0
}

/** Exposed for unit tests */
export function __resetLivekitReconnectUxPolicyForTests(): void {
  resetLivekitReconnectUxPolicy()
}

export function __getLivekitReconnectUxPolicyInternalsForTests(): {
  lastModalAt: number
  retryableErrorFirstAt: number
  retryableErrorCount: number
} {
  return { lastModalAt, retryableErrorFirstAt, retryableErrorCount }
}

export function __setLivekitReconnectUxPolicyInternalsForTests(p: Partial<{
  lastModalAt: number
  retryableErrorFirstAt: number
  retryableErrorCount: number
}>): void {
  if (p.lastModalAt !== undefined) lastModalAt = p.lastModalAt
  if (p.retryableErrorFirstAt !== undefined) retryableErrorFirstAt = p.retryableErrorFirstAt
  if (p.retryableErrorCount !== undefined) retryableErrorCount = p.retryableErrorCount
}

function toBanner(decision: ReconnectUxDecision): ReconnectUxDecision {
  return {
    tier: 'banner',
    redux: {
      ...decision.redux,
      showPrompt: true,
      showReconnectionDialog: false,
    },
    notification: decision.notification,
  }
}

/**
 * If a modal was shown recently, downgrade to banner unless forceModal.
 */
export function applyModalDebounce(
  decision: ReconnectUxDecision,
  cfg: LivekitReconnectUxConfig,
  now: number,
  forceModal: boolean
): ReconnectUxDecision {
  if (decision.tier !== 'modal' || forceModal) return decision
  if (lastModalAt > 0 && now - lastModalAt < cfg.modalMinIntervalMs) {
    return toBanner(decision)
  }
  return decision
}

export function dispatchLivekitReconnectUx(
  decision: ReconnectUxDecision,
  options?: { forceModal?: boolean }
): void {
  const cfg = getLivekitReconnectUxConfig()
  const now = Date.now()
  const final = applyModalDebounce(decision, cfg, now, options?.forceModal ?? false)
  if (final.tier === 'modal') lastModalAt = now
  store.dispatch(setLiveKitReconnectState(final.redux))
  // Connection notifications are handled by ConnectionUnstableToast and
  // the LiveKit reconnect banner — no need for additional toast popups.
}

/** During exponential backoff: modal only from modalAfterAttempt onward */
export function decideAutoReconnectAttempt(
  cfg: LivekitReconnectUxConfig,
  attempt: number,
  maxAttempts: number
): ReconnectUxDecision {
  const threshold = Math.min(Math.max(1, cfg.modalAfterAttempt), maxAttempts + 1)
  const showModal = attempt >= threshold
  return {
    tier: showModal ? 'modal' : 'banner',
    redux: {
      showPrompt: true,
      showReconnectionDialog: showModal,
      message: showModal
        ? `Reconnecting… (attempt ${attempt} of ${maxAttempts})`
        : `Connection recovering… (attempt ${attempt} of ${maxAttempts})`,
      reconnectAttempt: attempt,
      maxAttempts,
    },
    notification: showModal
      ? undefined
      : {
          type: 'info',
          message: 'Connection unstable — retrying in the background.',
          duration: 4000,
        },
  }
}

export function decideMaxAttemptsReached(
  cfg: LivekitReconnectUxConfig,
  attempt: number,
  maxAttempts: number
): ReconnectUxDecision {
  const cooldownUntil = Date.now() + cfg.liveKitCooldownMs
  return {
    tier: 'modal',
    redux: {
      showPrompt: true,
      showReconnectionDialog: true,
      message: 'Auto-reconnect failed',
      cooldownUntil,
      reconnectAttempt: attempt,
      maxAttempts,
    },
    notification: {
      type: 'error',
      message: 'Video call could not reconnect automatically.',
      duration: 6000,
    },
  }
}

/** Hard disconnect but auto-retry is scheduled — non-blocking */
export function decideHardDisconnectAutoRetryPending(): ReconnectUxDecision {
  return {
    tier: 'banner',
    redux: {
      showPrompt: true,
      showReconnectionDialog: false,
      message: 'Connection dropped — reconnecting…',
    },
    notification: {
      type: 'info',
      message: 'Connection dropped. Reconnecting…',
      duration: 4500,
    },
  }
}

/** Hard disconnect with no auto-retry path */
export function decideHardDisconnectNoAutoRetry(message: string): ReconnectUxDecision {
  return {
    tier: 'modal',
    redux: {
      showPrompt: true,
      showReconnectionDialog: true,
      message,
    },
    notification: {
      type: 'error',
      message,
      duration: 8000,
    },
  }
}

export function decideReconnectingWatchdog(attempt: number, maxAttempts: number): ReconnectUxDecision {
  return {
    tier: 'modal',
    redux: {
      showPrompt: true,
      showReconnectionDialog: true,
      message: 'Connection timed out. Reconnecting…',
      reconnectAttempt: attempt,
      maxAttempts,
    },
    notification: {
      type: 'warning',
      message: 'Connection timed out. Trying to recover…',
      duration: 6000,
    },
  }
}

export function decidePostReconnectTrackMissing(kind: 'camera' | 'microphone'): ReconnectUxDecision {
  const isCam = kind === 'camera'
  return {
    tier: 'modal',
    redux: {
      showPrompt: true,
      showReconnectionDialog: true,
      message: isCam
        ? 'Camera may not be visible — tap Reconnect Now'
        : 'Microphone may not be working — tap Reconnect Now',
    },
    notification: {
      type: 'warning',
      message: isCam
        ? 'Your camera may not be visible to others. Use Reconnect Now.'
        : 'Your microphone may not be working for others. Use Reconnect Now.',
      duration: 12000,
    },
  }
}

export function decidePostReconnectBothTracksMissing(): ReconnectUxDecision {
  return {
    tier: 'modal',
    redux: {
      showPrompt: true,
      showReconnectionDialog: true,
      message: 'Camera and microphone may not be working — tap Reconnect Now',
    },
    notification: {
      type: 'warning',
      message:
        'Your camera and microphone may not be working for others. Use Reconnect Now.',
      duration: 12000,
    },
  }
}

/**
 * Colyseus LIVEKIT_ERROR: retryable → banner first; second within window → modal.
 * Non-retryable → modal immediately.
 */
export function dispatchLiveKitServerError(rawMessage: string, retryable: boolean): void {
  const cfg = getLivekitReconnectUxConfig()
  const now = Date.now()
  let forceModal = !retryable

  if (retryable) {
    if (retryableErrorFirstAt === 0 || now - retryableErrorFirstAt > cfg.retryableErrorEscalateWindowMs) {
      retryableErrorFirstAt = now
      retryableErrorCount = 1
    } else {
      retryableErrorCount += 1
    }
    if (retryableErrorCount >= 2) forceModal = true
  } else {
    retryableErrorFirstAt = 0
    retryableErrorCount = 0
  }

  const serverText = rawMessage?.trim?.() ? rawMessage.trim() : ''
  const userMessage =
    retryable && !forceModal
      ? 'Video connection issue — recovering.'
      : retryable
        ? serverText || 'Video connection lost.'
        : serverText || 'LiveKit error'

  const decision: ReconnectUxDecision = {
    tier: forceModal ? 'modal' : 'banner',
    redux: {
      showPrompt: true,
      showReconnectionDialog: forceModal,
      message: userMessage,
    },
    notification: {
      type: forceModal ? 'error' : 'warning',
      message: userMessage,
      duration: forceModal ? 5000 : 6000,
    },
  }

  dispatchLivekitReconnectUx(decision, { forceModal })
}
